/**
 * Service layer — PDF Takeoffs
 *
 * Drawings live at the OPPORTUNITY level (a pursuit-stage Project); regions live
 * at the PROPOSAL level. Backed by Supabase (takeoff_drawings / takeoff_regions +
 * the takeoff-drawings Storage bucket) through /api/takeoffs — components never
 * call fetch directly.
 *
 * Upload model: uploadTempPdf stages the FULL client-sent PDF under tmp/ via a
 * signed URL; extractPages then splits the picked pages into single-page PDFs
 * server-side (pdf-lib) with a frame fingerprint each. Cancel = discardTemp.
 */

/**
 * Takeoff categories — one per measurable Fee Proposal input. `key` is EXACTLY
 * the Airtable Fee Proposals field name (must stay in sync with
 * PROPOSAL_INPUT_GROUPS in services/proposals.js — Phase 3 writes takeoff
 * totals back to these fields). `kind` picks the drawing tool:
 * area = polygon (SF), line = polyline (LF), count = point markers (qty).
 */
export const TAKEOFF_CATEGORIES = [
  // kind 'area' — polygon, SF
  { key: 'New Occupiable Area', label: 'New occupiable', kind: 'area' },
  { key: 'Alter Occupiable Area', label: 'Alter occupiable', kind: 'area' },
  { key: 'New Garage Area', label: 'New garage', kind: 'area' },
  { key: 'Alter Garage Area', label: 'Alter garage', kind: 'area' },
  { key: 'New Storage Utility Area', label: 'New storage/utility', kind: 'area' },
  { key: 'Alter Storage Utility Area', label: 'Alter storage/utility', kind: 'area' },
  { key: 'New Elevated Deck Area', label: 'New elevated deck', kind: 'area' },
  { key: 'Alter Elevated Deck Area', label: 'Alter elevated deck', kind: 'area' },
  { key: 'New Covered Patio Area', label: 'New covered patio', kind: 'area' },
  { key: 'Alter Covered Patio Area', label: 'Alter covered patio', kind: 'area' },
  { key: 'Hardscape Area', label: 'Hardscape', kind: 'area' },
  { key: 'Alter Roofing Area', label: 'Alter roofing', kind: 'area' },
  { key: 'Pool Spa Area', label: 'Pool/spa area', kind: 'area' },
  { key: 'Existing Area for As built', label: 'Existing (as-built)', kind: 'area' },
  // kind 'line' — polyline, LF
  { key: 'Alter Exterior Wall Length', label: 'Alter exterior wall', kind: 'line' },
  { key: 'Site Retaining Wall Length', label: 'Retaining wall', kind: 'line' },
  // kind 'count' — point markers
  { key: 'Pool Quantity', label: 'Pools', kind: 'count' },
  { key: 'Spa Quantity', label: 'Spas', kind: 'count' },
]

const DRAWING_TYPES = [
  { value: 'site_plan',   label: 'Site plan' },
  { value: 'floor_level', label: 'Floor level' },
  { value: 'roof_plan',   label: 'Roof plan' },
  { value: 'elevation',   label: 'Elevation' },
  { value: 'other',       label: 'Other' },
]
export { DRAWING_TYPES }

/** Human label for a drawing_type value ('site_plan' → 'Site plan'). */
export function drawingTypeLabel(value) {
  const t = DRAWING_TYPES.find(t => t.value === value)
  return t ? t.label : (value || '')
}

async function takeoffsApi(action, params = {}) {
  const token = await window.__clerkGetToken()
  const res = await fetch('/api/takeoffs', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ action, ...params }),
  }).then(r => r.json())
  if (res.error) throw new Error(res.error)
  return res
}

/* ── Drawings ─────────────────────────────────────────────────────────────── */

/**
 * All takeoff drawings for an opportunity, sorted.
 * @param {string} opportunityId — Airtable Project record ID (pursuit stage)
 * @returns {Promise<object[]>}
 */
export async function listDrawings(opportunityId) {
  const res = await takeoffsApi('listDrawings', { opportunityId })
  return res.drawings || []
}

/**
 * Stage a full client-sent PDF under tmp/ in the takeoff-drawings bucket.
 * Uses a signed URL so the browser uploads directly (avoids Vercel body limits).
 * @param {File} file
 * @returns {Promise<string>} tmpPath to pass to extractPages / discardTempPdf
 */
export async function uploadTempPdf(file) {
  const { signedUrl, tmpPath } = await takeoffsApi('getUploadUrl', { filename: file.name })
  const uploadRes = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/pdf', 'x-upsert': 'true' },
    body: file,
  })
  if (!uploadRes.ok) throw new Error(`Storage upload failed (${uploadRes.status})`)
  return tmpPath
}

/**
 * Extract the picked pages of a staged temp PDF into single-page takeoff drawings.
 * @param {Object} opts
 * @param {string} opts.opportunityId
 * @param {string} opts.tmpPath — from uploadTempPdf
 * @param {string} opts.sourcePdfName — original filename (stored on each drawing)
 * @param {Array<{page:number, name:string, drawingType?:string}>} opts.pages
 * @returns {Promise<object[]>} the created takeoff_drawings rows
 */
export async function extractPages({ opportunityId, tmpPath, sourcePdfName, pages }) {
  const res = await takeoffsApi('extractPages', { opportunityId, tmpPath, sourcePdfName, pages })
  return res.drawings || []
}

/**
 * Best-effort cleanup of a staged temp PDF when the user cancels the page picker.
 * Never throws — an orphaned tmp file is not worth surfacing an error for.
 */
export async function discardTempPdf(tmpPath) {
  try { await takeoffsApi('discardTemp', { tmpPath }) } catch (_) {}
}

/**
 * Update a drawing. Server whitelist: name, drawing_type, sort_order,
 * scale_points, scale_points_per_foot.
 * @returns {Promise<object>} the updated row
 */
export async function updateDrawing(id, fields) {
  const res = await takeoffsApi('updateDrawing', { id, fields })
  return res.drawing
}

/** Delete a drawing (its regions cascade; the stored PDF is removed best-effort). */
export async function deleteDrawing(id) {
  return takeoffsApi('deleteDrawing', { id })
}

/* ── Regions (Phase 2 UI consumes these) ──────────────────────────────────── */

/**
 * All takeoff regions for a proposal.
 * @param {string} proposalId — Airtable Fee Proposal record ID
 * @returns {Promise<object[]>}
 */
export async function listRegions(proposalId) {
  const res = await takeoffsApi('listRegions', { proposalId })
  return res.regions || []
}

/**
 * Create a region on a drawing for a proposal.
 * @param {Object} opts
 * @param {string} opts.drawingId — takeoff_drawings UUID
 * @param {string} opts.proposalId — Airtable Fee Proposal record ID
 * @param {string} opts.category — proposal field key (e.g. 'New Occupiable Area')
 * @param {'area'|'line'|'count'} opts.kind
 * @param {Array<[number,number]>} opts.points — visual-page fractions [fx, fy]
 * @param {number} [opts.computedValue]
 * @param {string} [opts.label]
 * @returns {Promise<object>} the created row
 */
export async function createRegion({ drawingId, proposalId, category, kind, points, computedValue, label }) {
  const res = await takeoffsApi('createRegion', { drawingId, proposalId, category, kind, points, computedValue, label })
  return res.region
}

/**
 * Update a region. Server whitelist: category, kind, points, computed_value, label.
 * @returns {Promise<object>} the updated row
 */
export async function updateRegion(id, fields) {
  const res = await takeoffsApi('updateRegion', { id, fields })
  return res.region
}

/** Delete a region. */
export async function deleteRegion(id) {
  return takeoffsApi('deleteRegion', { id })
}

/* ── Proposal-level sums + copy-forward (Phase 3) ─────────────────────────── */

/**
 * Aggregate regions into per-category sums (pure — no fetch). Regions span all
 * of the opportunity's drawings; the proposal-level total is cross-drawing.
 * Totals are rounded to whole numbers to match how manual entry writes the
 * Airtable number fields (toProposalFields sends Number(v)).
 * @param {object[]} regions — rows from listRegions
 * @returns {{ [categoryKey]: { total:number, count:number, kind:'area'|'line'|'count' } }}
 */
export function computeTakeoffSums(regions) {
  const out = {}
  for (const r of regions || []) {
    const key = r.category
    if (!key) continue
    if (!out[key]) {
      const def = TAKEOFF_CATEGORIES.find(c => c.key === key)
      out[key] = { total: 0, count: 0, kind: (def && def.kind) || r.kind || 'area' }
    }
    out[key].count += 1
    const v = Number(r.computed_value)
    if (!isNaN(v)) out[key].total += v
  }
  for (const k of Object.keys(out)) out[k].total = Math.round(out[k].total)
  return out
}

/**
 * Per-category takeoff totals for a proposal, summed across all drawings.
 * @param {string} proposalId — Airtable Fee Proposal record ID
 * @returns {Promise<{ [categoryKey]: { total:number, count:number, kind:string } }>}
 */
export async function getTakeoffSums(proposalId) {
  return computeTakeoffSums(await listRegions(proposalId))
}

/**
 * Duplicate every region from one proposal to another (copy-forward when a new
 * proposal starts from the opportunity's most recent prior proposal).
 * @returns {Promise<{ copied:number }>}
 */
export async function copyRegions({ fromProposalId, toProposalId }) {
  return takeoffsApi('copyRegions', { fromProposalId, toProposalId })
}
